import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  CircularProgress,
  Alert,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import axios from "axios";
import { BASE_URL } from "../../config/config";

export default function PaymentSuccess() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [verified, setVerified] = useState(false);
  const [error, setError] = useState("");
  const [paymentDetails, setPaymentDetails] = useState(null);

  useEffect(() => {
    const verifyPayment = async () => {
      // Get SafePay callback parameters
      const tracker = searchParams.get("tracker");
      const sig = searchParams.get("sig");
      const ref = searchParams.get("ref");

      // Get stored payment info for display
      const pendingPayment = localStorage.getItem("pending_payment");
      if (pendingPayment) {
        try {
          const paymentInfo = JSON.parse(pendingPayment);
          setPaymentDetails(paymentInfo);
                  } catch (e) {
          // Error parsing payment info
        }
      }

      // Verify payment with backend if we have tracker
      if (tracker) {
        try {
          const response = await axios.post(`${BASE_URL}/safepay/verify-payment`, {
            tracker,
            sig,
            reference: ref,
          });

          if (response.data.success) {
            setVerified(true);
            // Clear pending payment from storage
            localStorage.removeItem("pending_payment");
          } else {
            setError(response.data.message || "Payment verification failed.");
          }
        } catch (err) {
          
          setError("Could not verify payment. Please contact support.");
        }
      } else {
        // No tracker param - check if we have payment info
        if (pendingPayment) {
          setVerified(true);
          localStorage.removeItem("pending_payment");
        }
      }

      setLoading(false);
    };

    verifyPayment();
  }, [searchParams]);

  const getBillTypeName = (type) => {
    const names = {
      kelectric: "K-Electric Bill",
      ssgc: "SSGC Gas Bill",
      water: "Water Bill",
      maintainance: "Maintenance Charges",
      trash: "Trash Charges",
      rent: "Monthly Rent",
      security: "Security Charges",
      fixedCharges: "Fixed Charges (Water, Security, Trash)",
      advance: "Advance / Security Deposit",
    };
    return names[type] || type;
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "100vh",
          bgcolor: "#f5f5f5",
        }}
      >
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ mt: 2 }}>
          Verifying payment...
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "100vh",
        bgcolor: "#f5f5f5",
        p: 2,
      }}
    >
      <Card sx={{ maxWidth: 500, width: "100%", textAlign: "center" }}>
        <CardContent sx={{ p: 4 }}>
          {verified ? (
            <>
              <CheckCircleIcon
                sx={{ fontSize: 80, color: "#4CAF50", mb: 2 }}
              />
              <Typography variant="h4" fontWeight="bold" sx={{ mb: 2 }}>
                Payment Successful!
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                Your payment has been verified and processed successfully.
              </Typography>

              {paymentDetails && (
                <Box
                  sx={{
                    bgcolor: "#f5f5f5",
                    p: 2,
                    borderRadius: 2,
                    mb: 3,
                  }}
                >
                  {paymentDetails.flatName && (
                    <>
                      <Typography variant="subtitle2" color="text.secondary">
                        Property
                      </Typography>
                      <Typography variant="body1" fontWeight="bold" sx={{ mb: 1 }}>
                        {paymentDetails.flatName}
                      </Typography>
                    </>
                  )}

                  <Typography variant="subtitle2" color="text.secondary">
                    Payment Type
                  </Typography>
                  <Typography variant="body1" fontWeight="bold" sx={{ mb: 1 }}>
                    {getBillTypeName(paymentDetails.billType)}
                  </Typography>

                  <Typography variant="subtitle2" color="text.secondary">
                    Amount Paid
                  </Typography>
                  <Typography
                    variant="h5"
                    fontWeight="bold"
                    sx={{ color: "#4CAF50" }}
                  >
                    PKR {parseFloat(paymentDetails.amount || 0).toLocaleString()}
                  </Typography>
                </Box>
              )}

              <Alert severity="success" sx={{ mb: 3, textAlign: "left" }}>
                Payment verified! Landlord has been notified. You can track your request status.
              </Alert>

              <Box sx={{ display: "flex", gap: 2, justifyContent: "center" }}>
                <Button
                  variant="contained"
                  size="large"
                  onClick={() => navigate("/my-requests")}
                >
                  View My Requests
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  onClick={() => navigate("/tenant-dashboard")}
                >
                  Go to Dashboard
                </Button>
              </Box>
            </>
          ) : (
            <>
              <ErrorIcon sx={{ fontSize: 80, color: "#f44336", mb: 2 }} />

              <Typography variant="h5" sx={{ mb: 2 }}>
                Payment Verification Failed
              </Typography>

              {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                  {error}
                </Alert>
              )}

              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                We could not verify your payment. If money was deducted from your account,
                please contact support with your transaction details.
              </Typography>

              {paymentDetails && (
                <Box sx={{ bgcolor: "#fff3e0", p: 2, borderRadius: 2, mb: 3 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Payment Details
                  </Typography>
                  <Typography variant="body1">
                    {paymentDetails.flatName} - PKR {parseFloat(paymentDetails.amount || 0).toLocaleString()}
                  </Typography>
                </Box>
              )}

              <Box sx={{ display: "flex", gap: 2, justifyContent: "center" }}>
                <Button
                  variant="contained"
                  size="large"
                  onClick={() => navigate("/my-requests")}
                >
                  View My Requests
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  onClick={() => navigate("/tenant-dashboard")}
                >
                  Go to Dashboard
                </Button>
              </Box>
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}





